import React, { useState, ChangeEvent, useEffect } from "react";

import { useNavigate, useLocation } from "react-router-dom";

import { AuthenticationForm } from "@components/ui";
import { Field as FormField } from "@components/ui";


import AccountService from "@features/createaccount/service/AccountService";

import { PagePaths } from "@shared/constants";
import { ComposeURLWithGetQueryArguments } from "@shared/utils";

import { useTranslation } from "react-i18next";
import { useDocumentTitle } from "@shared/hooks/useDocumentTitle";
import { t_createPassword } from "@i18n/usualTexts/t_createPassword";
import { cleanFormDataErrors } from "@shared/utils";
import { delay } from "@shared/utils";
import { showAuthError } from "@features/createaccount/service/RegisterShowError";
// Note: useAuthentication and useOAuth2 are not needed in this component
// as RecoveryPasswordPage handles password reset with token/code from URL
import { t_recoveryPassword } from "@i18n/usualTexts/t_recoveryPassword";
import { ActionButton } from "@components/ui";
import { useRecaptchaContext } from "@shared/contexts/RecaptchaContext";
import { useIsMobileButtons } from "@shared/hooks/useIsMobileButtons";
import { isTimeoutError, isNetworkError } from "@features/authentication/services/OAuth2Request";
import ErrorPage from "@pages/ErrorPage";

export interface FormData {
  password: {
    value: string;
    errors: Set<string>;
  };
  confirmPassword: {
    value: string;
    errors: Set<string>;
  };
}

const RecoveryPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  useDocumentTitle(t("login.recoveryPassword.pageTitle"));
  const isMobileButtons = useIsMobileButtons();
  const [submitted, setSubmitted] = useState(false);
  const [serverErrorPopUp, setServerErrorPopUp] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { currentToken: antiBotToken, executeRecaptcha } = useRecaptchaContext();
  const [errorPageState, setErrorPageState] = useState<{
    errorType: "route" | "oauth" | "general" | "timeout";
    errorCode?: string;
    errorMessage?: string;
    errorTitle?: string;
  } | null>(null); // Track if we should render ErrorPage

  // Note: authData and state are not used in this component
  // as RecoveryPasswordPage handles password reset with token/code from URL

  // Helper function to handle malformed URL where token is concatenated to path
  // Expected: /login/recoverypassword?token=abc&code=xyz
  // Actual: /login/recoverypasswordabc&code=xyz
  function handleMalformedUrl(currentPath: string, searchParams: URLSearchParams): boolean {
    const RECOVERY_PASSWORD_PATH = '/login/recoverypassword';
    const isMalformed = currentPath.startsWith(RECOVERY_PASSWORD_PATH) && 
                        currentPath.length > RECOVERY_PASSWORD_PATH.length;
    
    if (!isMalformed) {
      return false;
    }
    
    const malformedPart = currentPath.substring(RECOVERY_PASSWORD_PATH.length);
    console.log('[RecoveryPassword] Malformed URL detected, extracting token from path:', malformedPart);
    
    const tokenMatch = malformedPart.match(/^([a-f0-9]+)/);
    if (!tokenMatch) {
      return false;
    }
    
    const token = tokenMatch[1];
    console.log('[RecoveryPassword] Extracted token:', token);
    
    if (!searchParams.has('token')) {
      searchParams.set('token', token);
    }
    
    const correctPath = RECOVERY_PASSWORD_PATH;
    const newUrl = `${correctPath}?${searchParams.toString()}`;
    console.log('[RecoveryPassword] Redirecting to correct URL:', newUrl);
    navigate(newUrl, { replace: true });
    return true;
  }

  // Helper function to validate required parameters
  function validateRecoveryParameters(searchParams: URLSearchParams): boolean {
    const code = searchParams.get('code');
    
    if (!code) {
      console.error('[RecoveryPassword] Missing required parameter - code:', !!code);
      setErrorPageState({
        errorType: 'oauth',
        errorCode: 'INVALID_RECOVERY_LINK',
        errorMessage: t('error.recoveryPassword.missingToken'),
        errorTitle: t('error.recoveryPassword.invalidLinkTitle')
      });
      return false;
    }
    
    const token = searchParams.get('token');
    console.log('[RecoveryPassword] Valid recovery link parameters found - code:', !!code, 'token:', !!token);
    return true;
  }

  // Extract token and code from malformed URL
  useEffect(() => {
    const currentPath = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    
    console.log('[RecoveryPassword] Current path:', currentPath);
    console.log('[RecoveryPassword] Search params:', location.search);
    
    if (handleMalformedUrl(currentPath, searchParams)) {
      return;
    }
    
    validateRecoveryParameters(searchParams);
  }, [location.pathname, location.search, navigate]);
  const [formData, setFormData] = useState<FormData>({
    password: {
      value: "",
      errors: new Set<string>(),
    },
    confirmPassword: {
      value: "",
      errors: new Set<string>(),
    },
  });

  // Render ErrorPage if error state is set
  if (errorPageState) {
    return (
      <ErrorPage
        errorType={errorPageState.errorType}
        errorCode={errorPageState.errorCode}
        errorMessage={errorPageState.errorMessage}
        errorTitle={errorPageState.errorTitle}
      />
    );
  }

  const submitButton = <ActionButton submit isLoading={isLoading} className={isMobileButtons ? "h-12" : undefined} />;

  const mobileFooter = isMobileButtons ? (
    <div className="text-end w-full">{submitButton}</div>
  ) : undefined;

  return (
    <AuthenticationForm
      title={t(t_recoveryPassword.title)}
      subtitle={t(t_recoveryPassword.subtitle)}
      onSubmit={() => handleSubmit()}
      isLoading={isLoading}
      serverErrorPopUp={serverErrorPopUp}
      setServerErrorPopUp={setServerErrorPopUp}
      mobileFooter={mobileFooter}
    >
      <div>
        <FormField
          autofocus={true}
          name="password"
          type="password"
          required={true}
          value={formData.password.value}
          errors={formData.password.errors}
          onChange={handleFormData}
          submitted={submitted}
        >
          {t(t_createPassword.passwordPlaceholder)}
        </FormField>
      </div>

      <div className="mt-4 px-0 m-0">
        <FormField
          name="confirmPassword"
          nameconfirm="password"
          type="password"
          required={true}
          value={formData.confirmPassword.value}
          errors={formData.confirmPassword.errors}
          onChange={handleFormData}
          submitted={submitted}
        >
          {t(t_createPassword.confirmPasswordPlaceholder)}
        </FormField>
      </div>

      {!isMobileButtons && (
        <div>
          <div className="text-end flex items-end justify-end">
            {submitButton}
          </div>
        </div>
      )}
    </AuthenticationForm>
  );

  function handleFormData(e: ChangeEvent<HTMLInputElement>) {
    const { value, name } = e.target;

    formData[name as keyof FormData].value = value;
    setFormData({
      ...formData,
    });
  }

  // Helper functions to extract error properties (improves testability)
  function getErrorMessage(error: any): string | undefined {
    return error && typeof error === 'object' && 'message' in error ? error.message : undefined;
  }

  function getErrorBody(error: any): any {
    return error && typeof error === 'object' && 'body' in error ? error.body : null;
  }

  function getErrorStatus(error: any): number | undefined {
    return error && typeof error === 'object' && 'status' in error ? error.status : undefined;
  }

  function getErrorBodyProperty(errorBody: any, property: string): any {
    return errorBody && typeof errorBody === 'object' && property in errorBody ? errorBody[property] : undefined;
  }

  function getBackendErrorMessage(errorBody: any): string | undefined {
    if (!errorBody || typeof errorBody !== 'object') {
      return undefined;
    }
    const detail = getErrorBodyProperty(errorBody, 'detail');
    const message = getErrorBodyProperty(errorBody, 'message');
    return detail || message || undefined;
  }

  // Helper function to handle timeout errors
  function handleTimeoutError(error: any): boolean {
    if (!isTimeoutError(error)) {
      return false;
    }
    const errorMessage = getErrorMessage(error);
    setErrorPageState({
      errorType: 'timeout',
      errorMessage: errorMessage || t('error.runtime.requestTimeout')
    });
    return true;
  }

  // Helper function to handle network errors
  function handleNetworkError(error: any): boolean {
    if (!isNetworkError(error)) {
      return false;
    }
    const errorMessage = getErrorMessage(error);
    setErrorPageState({
      errorType: 'general',
      errorMessage: errorMessage || t('error.runtime.networkError')
    });
    return true;
  }

  // Helper function to handle backend errors
  function handleBackendError(error: any): boolean {
    const errorBody = getErrorBody(error);
    const errorStatus = getErrorStatus(error);
    
    if (!errorBody && !errorStatus) {
      return false;
    }
    
    setErrorPageState({
      errorType: 'oauth',
      errorCode: getErrorBodyProperty(errorBody, 'error_code'),
      errorTitle: getErrorBodyProperty(errorBody, 'title'),
      errorMessage: getBackendErrorMessage(errorBody)
    });
    return true;
  }

  // Helper function to handle errors and determine if ErrorPage should be shown
  function handleError(error: any) {
    console.error('[RecoveryPassword] Error handling:', error);
    
    if (handleTimeoutError(error)) {
      return;
    }
    
    if (handleNetworkError(error)) {
      return;
    }
    
    if (handleBackendError(error)) {
      return;
    }
    
    // For other errors, show modal (legacy behavior for non-critical errors)
    const errorCopy = JSON.parse(JSON.stringify(error));
    showAuthError(errorCopy, setServerErrorPopUp, frontExceptionFunction, t);
  }

  async function handleSubmit() {
    setSubmitted(true);
    setIsLoading(true);
    setErrorPageState(null); // Clear any previous error page state

    try {
      checkThisFormData();

      await post_and_navigate();
    } catch (e) {
      handleError(e);
    } finally {
      setIsLoading(false);

      setFormData({
        ...formData,
      });

      await delay(1000);
      setSubmitted(false);
    }
  }

  async function post_and_navigate() {
    // Get token and code from URL parameters
    // The 'code' parameter is a JWT token that serves as the recovery token
    // If 'token' is not present, we use 'code' as the token
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    const code = searchParams.get('code');
    
    // Use code as token if token is not present
    // The code JWT is the recovery token that will be used for authentication
    const recoveryToken = token || code;
    
    if (!recoveryToken) {
      throw new Error(t("error.runtime.missingRecoveryParams"));
    }

    console.log('[RecoveryPassword] Using recovery token for password reset - token:', !!token, 'code:', !!code);

    let freshAntiBotToken = antiBotToken;
    if (!freshAntiBotToken && executeRecaptcha) {
      const result = await executeRecaptcha();
      if (result.success && result.token) {
        freshAntiBotToken = result.token;
      }
    }

    const { RecoveryPasswordRequest } = await import('@features/authentication/services/OAuth2Request');
    
    const recoveryPasswordResponse = await RecoveryPasswordRequest(
      formData.password.value,
      formData.confirmPassword.value,
      recoveryToken,
      freshAntiBotToken || undefined
    );
    
    console.log('[RecoveryPassword] Password reset successful:', recoveryPasswordResponse);

    // Redirect to identity page after successful password reset
    navigate(ComposeURLWithGetQueryArguments(PagePaths.LOGIN.IDENTITY, { 
      success: 'password_reset' 
    }));
  }

  function checkThisFormData() {
    cleanFormDataErrors(formData);

    const accountService: AccountService = new AccountService(t);
    accountService.checkPassword(formData.password.value, "password");
    accountService.checkConfirmPassword(
      formData.confirmPassword.value,
      formData.password.value,
      "confirmPassword"
    );
  }

  function frontExceptionFunction(errorDescription: Array<string>) {
    formData[errorDescription[0] as keyof FormData].errors.add(
      errorDescription[1]
    );
  }
};

export default RecoveryPasswordPage;
